<script lang="ts">
  import { onMount } from 'svelte';
  import { ACHIEVEMENTS } from '@arena/shared';
  import { click } from '../game/Sfx';
  import {
    authClient,
    deleteAccount,
    signInWithDiscord,
    signOut,
  } from '../lib/auth';
  import { chord, isModifier } from '../lib/keys';
  import { app } from '../lib/net.svelte';
  import { REGIONS, goToRegion, region } from '../lib/region';
  import {
    BINDINGS,
    COMBAT_TEXT,
    keyLabel,
    resetKeys,
    settings,
    type CombatTextKind,
  } from '../lib/settings.svelte';

  let { onclose }: { onclose: () => void } = $props();
  let tab = $state<
    | 'interface'
    | 'graphics'
    | 'combat'
    | 'camera'
    | 'audio'
    | 'keys'
    | 'account'
    | 'dev'
    | 'about'
  >('interface');
  /** `press`: a combo with Shift/Ctrl/Alt may bind; movement is held, so it takes bare keys only. */
  let waiting = $state<{ id: string; slot: number; press: boolean } | null>(
    null,
  );
  let joined = $state<Date | null>(null);
  let config = $state({ discord: false, dev: false });
  let confirming = $state(false);
  let typed = $state('');
  let deleting = $state(false);
  let deleteError = $state('');

  const wins = $derived(app.ratings.reduce((n, r) => n + r.wins, 0));
  const losses = $derived(app.ratings.reduce((n, r) => n + r.losses, 0));
  onMount(() => {
    click();
    authClient.getSession().then(({ data }) => {
      if (data) joined = new Date(data.user.createdAt);
    });
    fetch('/api/config')
      .then((r) => r.json())
      .then((c) => (config = c));
  });

  async function logOut() {
    if (
      app.user?.guest &&
      !confirm('Guest accounts cannot be recovered. Sign out anyway?')
    )
      return;
    await signOut();
    location.reload();
  }

  function closeConfirm() {
    confirming = false;
    typed = '';
    deleteError = '';
  }

  async function removeAccount() {
    deleting = true;
    try {
      await deleteAccount();
      location.reload();
    } catch (error) {
      deleteError = (error as Error).message;
      deleting = false;
    }
  }

  const VOLUMES = [
    ['volume', 'Master', ''],
    ['effectsVolume', 'Effects', 'Spells, weapons, footsteps'],
    ['ambienceVolume', 'Ambience', 'Background sound of the hub and arenas'],
    ['musicVolume', 'Music', 'The hub and arena soundtracks'],
    ['interfaceVolume', 'Interface', 'Button clicks, menus'],
  ] as const;

  const PRESETS = {
    Low: { renderScale: 0.6, antialias: false, shadows: 'off', effects: 'low' },
    Medium: {
      renderScale: 0.8,
      antialias: true,
      shadows: 'low',
      effects: 'full',
    },
    High: { renderScale: 1, antialias: true, shadows: 'high', effects: 'full' },
  } as const;

  const FIXED: [string, string][] = [
    ['Right drag', 'Steer'],
    ['Left drag', 'Look around'],
    ['Both buttons', 'Run forward'],
    ['Wheel', 'Zoom'],
    ['Click', 'Target a unit'],
    ['Esc', 'Stop casting / clear target / settings'],
  ];

  /** A key does one thing: binding it takes it off every other action. */
  function bind(id: string, slot: number, code: string | null) {
    const keys = [...settings.keys[id]];
    keys[slot] = code ?? '';
    if (code)
      for (const other of Object.keys(settings.keys))
        settings.keys[other] = settings.keys[other].filter((c) => c !== code);
    settings.keys[id] = keys.filter((c, i) => c && keys.indexOf(c) === i);
  }

  /** Steps of 5%, rounded so repeated clicks don't drift off the grid. */
  function scale(dir: number) {
    const next = Math.round((settings.uiScale + dir * 0.05) * 20) / 20;
    settings.uiScale = Math.min(1.5, Math.max(0.6, next));
  }

  /** Runs before the game's own key handler so a key being bound never reaches it. */
  function onKey(event: KeyboardEvent) {
    if (!waiting && event.code !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    if (!waiting) {
      click();
      return confirming ? closeConfirm() : onclose();
    }
    // A modifier binds on its own when released, unless another key goes down first.
    if (isModifier(event.code)) return;
    if (event.code !== 'Escape')
      bind(
        waiting.id,
        waiting.slot,
        event.code === 'Backspace'
          ? null
          : waiting.press
            ? chord(event)
            : event.code,
      );
    waiting = null;
  }

  function onKeyUp(event: KeyboardEvent) {
    if (!waiting || !isModifier(event.code)) return;
    bind(waiting.id, waiting.slot, event.code);
    waiting = null;
  }
</script>

<svelte:window onkeydowncapture={onKey} onkeyupcapture={onKeyUp} />

<section class="panel settings">
  <header>
    <img class="wordmark" src="/wordmark/stacked.webp" alt="Sandsworn" />
    <h2>Settings</h2>
    <button class="close" onclick={onclose} aria-label="Close">×</button>
  </header>
  <nav class="tabs">
    <button
      class:active={tab === 'interface'}
      onclick={() => (tab = 'interface')}>Interface</button
    >
    <button class:active={tab === 'graphics'} onclick={() => (tab = 'graphics')}
      >Graphics</button
    >
    <button class:active={tab === 'combat'} onclick={() => (tab = 'combat')}
      >Combat Text</button
    >
    <button class:active={tab === 'camera'} onclick={() => (tab = 'camera')}
      >Camera</button
    >
    <button class:active={tab === 'audio'} onclick={() => (tab = 'audio')}
      >Audio</button
    >
    <button class:active={tab === 'keys'} onclick={() => (tab = 'keys')}
      >Keybindings</button
    >
    <button class:active={tab === 'account'} onclick={() => (tab = 'account')}
      >Account</button
    >
    {#if config.dev}<button
        class="dev"
        class:active={tab === 'dev'}
        onclick={() => (tab = 'dev')}>Dev</button
      >{/if}
    <button
      class="about"
      class:active={tab === 'about'}
      onclick={() => (tab = 'about')}>About</button
    >
  </nav>

  <div class="content">
    {#if tab === 'interface'}
      <h3>Layout</h3>
      <div class="row">
        <span>UI scale</span>
        <button
          onclick={() => scale(-1)}
          disabled={settings.uiScale <= 0.6}
          aria-label="Decrease UI scale">−</button
        >
        <span class="value">{Math.round(settings.uiScale * 100)}%</span>
        <button
          onclick={() => scale(1)}
          disabled={settings.uiScale >= 1.5}
          aria-label="Increase UI scale">+</button
        >
      </div>
      <label class="row">
        <input type="checkbox" bind:checked={settings.lockBars} />
        <span>Lock action bars</span>
        <span class="muted small">Off: drag spells to rearrange the bar</span>
      </label>
      <label class="row">
        <input type="checkbox" bind:checked={settings.spellNames} />
        <span>Spell names on the action bar</span>
      </label>
      <button onclick={() => (settings.bars = {})}
        >Reset action bar layout</button
      >
      <label class="row">
        <input type="checkbox" bind:checked={settings.lockFrames} />
        <span>Lock frames</span>
        <span class="muted small"
          >Off: drag unit frames and the cast bar. Right-click your frame to
          toggle</span
        >
      </label>
      <button onclick={() => (settings.frames = {})}
        >Reset frame positions</button
      >
      <label class="row">
        <span>Bar text</span>
        <select bind:value={settings.statusText}>
          <option value="none">None</option>
          <option value="number">Number</option>
          <option value="both">Number and percent</option>
          <option value="percent">Percent</option>
        </select>
        <span class="muted small">On unit frames' health and resource bars</span
        >
      </label>
      <label class="row">
        <input type="checkbox" bind:checked={settings.targetOfTarget} />
        <span>Target of target</span>
        <span class="muted small"
          >Who your target is targeting, beside the target frame</span
        >
      </label>
      <label class="row">
        <input type="checkbox" bind:checked={settings.swingTimer} />
        <span>Swing timer</span>
        <span class="muted small"
          >Time to your next auto-attack, above the cast bar</span
        >
      </label>
      <label class="row">
        <input type="checkbox" bind:checked={settings.meter} />
        <span>Damage meter</span>
        <span class="muted small"
          >Your damage and healing by spell, in matches</span
        >
      </label>

      <h3>Alert text</h3>
      <label class="row">
        <input type="checkbox" bind:checked={settings.errors} />
        <span>Error messages</span>
        <span class="muted small">"Out of range", "Not enough mana"…</span>
      </label>
      <label class="row">
        <input type="checkbox" bind:checked={settings.lossOfControl} />
        <span>Loss of control alert</span>
        <span class="muted small">"Stunned", "Silenced", "Fire Locked"…</span>
      </label>
    {:else if tab === 'graphics'}
      <h3>Preset</h3>
      <div class="row">
        {#each Object.entries(PRESETS) as [name, preset] (name)}
          <button onclick={() => Object.assign(settings, preset)}>{name}</button
          >
        {/each}
      </div>
      <h3>Performance</h3>
      <label class="row">
        <span class="label">Render scale</span>
        <input
          type="range"
          min="0.5"
          max="1"
          step="0.05"
          bind:value={settings.renderScale}
        />
        <span class="value">{Math.round(settings.renderScale * 100)}%</span>
        <span class="muted small"
          >Lower draws the world at fewer pixels; the interface stays sharp</span
        >
      </label>
      <label class="row">
        <span>Frame rate limit</span>
        <select bind:value={settings.fpsCap}>
          <option value={0}>Unlimited</option>
          <option value={120}>120</option>
          <option value={60}>60</option>
          <option value={30}>30</option>
        </select>
        <span class="muted small">Saves battery and heat on laptops</span>
      </label>
      <label class="row">
        <span>Spell effects</span>
        <select bind:value={settings.effects}>
          <option value="full">Full</option>
          <option value="low">Low</option>
        </select>
        <span class="muted small">Low throws fewer particles per hit</span>
      </label>
      <label class="row">
        <input type="checkbox" bind:checked={settings.showFps} />
        <span>Show frame rate</span>
        <span class="muted small">Beside your ping</span>
      </label>
      <h3>Quality</h3>
      <p class="muted small">
        These apply the next time a map loads: entering or leaving a match, or
        reloading.
      </p>
      <label class="row">
        <span>Shadows</span>
        <select bind:value={settings.shadows}>
          <option value="high">High</option>
          <option value="low">Low</option>
          <option value="off">Off</option>
        </select>
      </label>
      <label class="row">
        <input type="checkbox" bind:checked={settings.antialias} />
        <span>Antialiasing</span>
        <span class="muted small">Smooths jagged edges</span>
      </label>
    {:else if tab === 'combat'}
      <h3>Floating combat text</h3>
      <label class="row">
        <input type="checkbox" bind:checked={settings.combatText} />
        <span>Show floating combat text</span>
        <span class="muted small">Numbers and alerts over units</span>
      </label>
      <h3>Show</h3>
      {#each Object.entries(COMBAT_TEXT) as [kind, label] (kind)}
        <label class="row">
          <input
            type="checkbox"
            disabled={!settings.combatText}
            bind:checked={settings.combatTextKinds[kind as CombatTextKind]}
          />
          <span>{label}</span>
        </label>
      {/each}
    {:else if tab === 'camera'}
      <h3>Follow</h3>
      <label class="row">
        <span>Swing behind character</span>
        <select bind:value={settings.cameraFollow}>
          <option value="moving">While moving</option>
          <option value="always">Always</option>
          <option value="never">Never</option>
        </select>
      </label>
    {:else if tab === 'audio'}
      <h3>Volume</h3>
      {#each VOLUMES as [key, label, hint] (key)}
        <label class="row">
          <span class="label">{label}</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            bind:value={settings[key]}
          />
          <span class="value">{Math.round(settings[key] * 100)}%</span>
          <span class="muted small">{hint}</span>
        </label>
      {/each}
    {:else if tab === 'account'}
      <h3>Profile</h3>
      <dl>
        <dt>Name</dt>
        <dd>
          {app.user?.name ?? '—'}{#if app.achievements.title}, <span
              class="rating">{ACHIEVEMENTS[app.achievements.title]?.title}</span
            >{/if}
        </dd>
        <dt>Account</dt>
        <dd>{app.user?.guest ? 'Guest' : 'Discord'}</dd>
        <dt>Joined</dt>
        <dd>
          {joined?.toLocaleDateString(undefined, { dateStyle: 'long' }) ?? '…'}
        </dd>
      </dl>
      {#if region}
        <label class="row">
          <span>Region</span>
          <select
            value={region}
            onchange={(e) => goToRegion(e.currentTarget.value)}
          >
            {#each Object.entries(REGIONS) as [id, label] (id)}
              <option value={id}>{label}</option>
            {/each}
          </select>
        </label>
        <p class="muted small">Each region has its own accounts and ratings.</p>
      {/if}

      <h3>Rated arenas</h3>
      <dl>
        {#each app.ratings as r (r.bracket)}
          <dt>{r.bracket}</dt>
          <dd>
            <span class="rating">{r.rating}</span>
            <span class="muted">{r.wins}W – {r.losses}L</span>
          </dd>
        {/each}
        <dt>Total</dt>
        <dd>
          {wins + losses} played
          <span class="muted"
            >{wins + losses ? Math.round((wins / (wins + losses)) * 100) : 0}%
            won</span
          >
        </dd>
      </dl>

      <h3>Session</h3>
      {#if app.user?.guest}
        <p class="muted small">
          You're playing as a guest: signing out loses this account and its
          record.{config.discord ? ' Sign in with Discord to keep it.' : ''}
        </p>
        {#if config.discord}<button
            onclick={() => signInWithDiscord(app.user?.name ?? '')}
            >Sign in with Discord</button
          >{/if}
      {/if}
      <button onclick={logOut}>Sign out</button>

      <h3>Delete account</h3>
      <p class="muted small">
        Permanently erases your account, ratings, achievements and titles. Your
        past matches stay in other players' history under "Deleted".
      </p>
      <button class="danger" onclick={() => (confirming = true)}
        >Delete account</button
      >
    {:else if tab === 'dev'}
      <h3>Tools</h3>
      <p><a href="#/editor">Map editor</a></p>
      <p><a href="#/viewer">Character viewer</a></p>
    {:else if tab === 'about'}
      <h3>Sandsworn</h3>
      <p>
        A small arena battler in the spirit of the classics: cast times,
        interrupts, crowd control, line of sight, and a lot of jumping around
        pillars. Queue up for 1v1, 2v2 or 3v3, or just hang out in the hub.
      </p>
      <p class="muted">Built by a small team, for the love of the game.</p>

      <h3>Credits</h3>
      <p>
        3D models by <a
          href="https://www.kenney.nl"
          target="_blank"
          rel="noreferrer">Kenney</a
        >, released under
        <a
          href="https://creativecommons.org/publicdomain/zero/1.0/"
          target="_blank"
          rel="noreferrer">CC0</a
        >:
      </p>
      <ul class="muted">
        <li>Mini Arena</li>
        <li>Mini Dungeon</li>
        <li>Graveyard Kit</li>
        <li>Castle Kit</li>
        <li>Pirate Kit</li>
        <li>Mini Forest</li>
        <li>Survival Kit</li>
        <li>Cube Pets</li>
      </ul>
      <p>Sound effects:</p>
      <ul class="muted">
        <li>Free Fantasy SFX Pack by TomMusic</li>
        <li>
          Magic Spell SFX by <a
            href="https://www.leonardocalvocomposer.com"
            target="_blank"
            rel="noreferrer">Leonardo Calvo</a
          >,
          <a
            href="https://creativecommons.org/licenses/by/4.0/"
            target="_blank"
            rel="noreferrer">CC BY 4.0</a
          >
        </li>
        <li>Kenney Impact Sounds and RPG Audio, CC0</li>
      </ul>
      <p class="muted small">
        Built with Three.js, Svelte, Rapier and Bun. Thanks, Kenney.
      </p>

      <h3>Legal</h3>
      <p>
        <a href="/terms.html" target="_blank">Terms of Service</a> ·
        <a href="/privacy.html" target="_blank">Privacy Policy</a> ·
        <a href="mailto:support@sandsworn.gg">support@sandsworn.gg</a>
      </p>
    {:else}
      <p class="muted small">
        Click a key, then press the new one; outside Movement it can be held
        with Shift, Ctrl or Alt. Esc cancels, Backspace unbinds.
      </p>
      {#each BINDINGS as [section, rows] (section)}
        <h3>{section}</h3>
        <dl>
          {#each rows as [id, label] (id)}
            <dt>{label}</dt>
            <dd>
              {#each [0, 1] as slot (slot)}
                {@const code = settings.keys[id]?.[slot]}
                <button
                  class="key"
                  class:waiting={waiting?.id === id && waiting.slot === slot}
                  onclick={(e) => {
                    e.currentTarget.blur();
                    waiting = { id, slot, press: section !== 'Movement' };
                  }}
                >
                  {waiting?.id === id && waiting.slot === slot
                    ? 'Press a key…'
                    : code
                      ? keyLabel(code)
                      : '—'}
                </button>
              {/each}
            </dd>
          {/each}
        </dl>
      {/each}
      <h3>Mouse and fixed keys</h3>
      <dl>
        {#each FIXED as [key, action] (action)}
          <dt>{action}</dt>
          <dd><kbd>{key}</kbd></dd>
        {/each}
      </dl>
      <button onclick={resetKeys}>Reset keybindings</button>
    {/if}
  </div>

  {#if confirming}
    <div
      class="confirm"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="delete-title"
    >
      <form
        class="box"
        onsubmit={(e) => {
          e.preventDefault();
          removeAccount();
        }}
      >
        <h3 id="delete-title">Delete {app.user?.name ?? 'account'}?</h3>
        <p>
          This can't be undone. Your account, ratings, achievements and titles
          are erased for good{app.user?.guest
            ? ''
            : ', and Sandsworn forgets your Discord sign-in'}.
        </p>
        <label>
          Type <b>delete</b> to confirm
          <input
            bind:value={typed}
            autocomplete="off"
            spellcheck="false"
            {@attach (input) => input.focus()}
          />
        </label>
        {#if deleteError}<p class="error">{deleteError}</p>{/if}
        <div class="row">
          <button type="button" onclick={closeConfirm}>Cancel</button>
          <button
            class="danger"
            disabled={typed.trim().toLowerCase() !== 'delete' || deleting}
            >{deleting ? 'Deleting…' : 'Delete forever'}</button
          >
        </div>
      </form>
    </div>
  {/if}
</section>

<style>
  .settings {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: grid;
    grid-template-columns: 140px minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr);
    width: min(680px, calc(100% - 24px));
    height: min(560px, calc(100% - 48px));
    padding: 0;
    pointer-events: auto;
    background: var(--panel-solid);
    border-color: var(--gold);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.6);
    font-size: 14px;
  }
  header {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 8px 18px;
    border-bottom: 1px solid var(--border);
  }
  .wordmark {
    height: 36px;
  }
  header h2 {
    margin: 0 auto 0 0;
  }
  .close {
    padding: 0 8px;
    font-size: 18px;
  }
  .tabs {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px 10px;
    border-right: 1px solid var(--border);
  }
  .tabs button {
    text-align: left;
    opacity: 0.6;
  }
  .tabs .dev,
  .tabs .about {
    margin-top: auto;
  }
  .tabs .dev + .about {
    margin-top: 0;
  }
  .tabs button.active {
    opacity: 1;
    border-color: var(--gold);
  }
  .content {
    overflow-y: auto;
    padding: 0 18px 14px;
  }
  h3 {
    margin: 14px 0 6px;
    padding-bottom: 4px;
    border-bottom: 1px solid var(--border);
    font-size: 13px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .row {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 8px;
  }
  .content > button {
    margin-bottom: 8px;
  }
  .value {
    min-width: 40px;
    text-align: center;
  }
  .label {
    min-width: 70px;
  }
  dl {
    display: grid;
    grid-template-columns: 1fr max-content;
    gap: 4px 14px;
    align-items: center;
    margin: 0 0 8px;
  }
  dd {
    display: flex;
    gap: 6px;
    margin: 0;
  }
  .key {
    min-width: 72px;
    padding: 2px 8px;
    font-size: 12px;
    font-weight: bold;
  }
  .key.waiting {
    color: var(--gold);
    border-style: dashed;
  }
  kbd {
    padding: 2px 7px;
    font: inherit;
    font-size: 12px;
    font-weight: bold;
    color: #fff3d6;
    background: linear-gradient(#3b3428, #221d15);
    border: 1px solid #6b5a35;
    border-bottom-width: 3px;
    border-radius: 4px;
  }
  .rating {
    color: var(--gold);
    font-weight: bold;
  }
  .muted {
    color: var(--muted);
  }
  .small {
    font-size: 12px;
  }
  .danger {
    color: var(--enemy);
    border-color: var(--enemy);
  }
  .error {
    color: var(--enemy);
  }
  .confirm {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    background: rgba(0, 0, 0, 0.6);
  }
  .confirm .box {
    width: min(360px, calc(100% - 32px));
    padding: 4px 18px 14px;
    background: var(--panel-solid);
    border: 1px solid var(--enemy);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.6);
  }
  .confirm label {
    display: grid;
    gap: 6px;
  }
  .confirm .row {
    justify-content: flex-end;
    margin: 14px 0 0;
  }
</style>
